<template>
  <div class="stats-date-range-filter">
    <div class="d-flex align-items-center mb-2">
      <!-- Quick date selection buttons -->
      <b-button-group class="quick-date-buttons">
        <b-button
          v-for="(button, index) in quickDateButtonsWithLabels"
          :key="index"
          size="sm"
          :variant="button.active ? 'outline-primary' : 'outline-secondary'"
          @click="selectQuickDate(button)"
        >
          {{ button.label }}
        </b-button>
      </b-button-group>
      <div class="col-picker d-flex align-items-center">
        <feather-icon
          icon="CalendarIcon"
          size="16"
        />
        <flat-pickr
          v-model="localRangePicker"
          :config="pickerConfig"
          class="form-control flat-picker bg-transparent border-0 shadow-none"
          :placeholder="placeholder"
          @on-close="handleRangeChange"
        />
      </div>
      <b-button
        v-b-tooltip.hover
        variant="outline-primary"
        size="sm"
        class="mr-1"
        :disabled="isLoading"
        :title="$t('form.refresh')"
        @click.stop="$emit('refresh')"
      >
        <feather-icon
          icon="RefreshCcwIcon"
          size="14"
        />
      </b-button>
      <slot name="additional-filters" />
    </div>
  </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component'
import { BButton, BButtonGroup, VBTooltip } from 'bootstrap-vue'
import getFormattedDate from '@/utils/date-formatter'
import resolveStatsDateFilter from '@/utils/stats-date-filter'

export default {
  name: 'StatsDateRangeFilter',
  components: {
    BButton,
    BButtonGroup,
    flatPickr,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    value: {
      type: Array,
      default: () => [getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)), getFormattedDate(new Date())],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'YYYY-MM-DD',
    },
    pickerConfig: {
      type: Object,
      default: () => ({
        mode: 'range',
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'Y-m-d',
        formatDate: date => getFormattedDate(new Date(date)),
      }),
    },
  },
  data() {
    return {
      localRangePicker: this.value,
      quickDateButtons: [
        { type: 'today', active: false },
        { type: 'yesterday', active: false },
        { type: '7days', active: false },
        { type: 'currentWeek', active: false },
        { type: 'currentMonth', active: true },
      ],
    }
  },
  computed: {
    quickDateButtonsWithLabels() {
      return this.quickDateButtons.map(btn => ({
        ...btn,
        label: this.$i18n.t(`stats.quick_dates.${btn.type}`),
      }))
    },
  },
  watch: {
    value: {
      handler(newVal) {
        this.localRangePicker = newVal
        this.updateActiveButton()
      },
      immediate: true,
    },
  },
  created() {
    this.loadSavedSettings()
  },
  methods: {
    handleRangeChange(selectedDates) {
      // Format dates to YYYY-MM-DD
      const formattedDates = Array.isArray(selectedDates)
        ? selectedDates.map(date => getFormattedDate(new Date(date)))
        : [getFormattedDate(new Date()), getFormattedDate(new Date())]
      this.localRangePicker = formattedDates
      this.$emit('input', formattedDates)
      this.$emit('range-change', formattedDates)
      this.updateActiveButton()
      this.quickDateButtons.forEach(button => { button.active = false })
      this.saveSettings()

      // Update URL without page reload
      const newQuery = { ...this.$route.query, from: formattedDates[0], to: formattedDates[1] }
      delete newQuery.datePreset
      const newPath = this.$route.path
      window.history.replaceState(
        {},
        '',
        `${newPath}?${new URLSearchParams(newQuery).toString()}`,
      )
    },

    selectQuickDate(button) {
      // Deactivate all buttons
      this.quickDateButtons.forEach(btn => {
        btn.active = false
      })

      // Activate selected button
      button.active = true

      // Calculate date range based on button type
      const { from, to } = this.calculateDateRange(button.type)
      this.localRangePicker = [from, to]

      // Emit changes
      this.$emit('input', this.localRangePicker)
      this.$emit('range-change', this.localRangePicker)

      // Save settings
      this.saveSettings()

      // Update URL without page reload
      const newQuery = {
        ...this.$route.query,
        from: this.localRangePicker[0],
        to: this.localRangePicker[1],
        datePreset: button.type,
      }
      const newPath = this.$route.path
      window.history.replaceState(
        {},
        '',
        `${newPath}?${new URLSearchParams(newQuery).toString()}`,
      )
    },

    calculateDateRange(type) {
      const today = new Date()
      let from
      let to

      switch (type) {
        case 'today': {
          from = getFormattedDate(today)
          to = getFormattedDate(today)
          break
        }
        case 'yesterday': {
          const yesterday = new Date(today)
          yesterday.setDate(yesterday.getDate() - 1)
          from = getFormattedDate(yesterday)
          to = getFormattedDate(yesterday)
          break
        }
        case '7days': {
          const sevenDaysAgo = new Date(today)
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6)
          from = getFormattedDate(sevenDaysAgo)
          to = getFormattedDate(today)
          break
        }
        case 'currentWeek': {
          const startOfWeek = new Date(today)
          const dayOfWeek = startOfWeek.getDay()
          startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek)
          from = getFormattedDate(startOfWeek)
          to = getFormattedDate(today)
          break
        }
        case 'currentMonth': {
          const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
          from = getFormattedDate(startOfMonth)
          to = getFormattedDate(today)
          break
        }
        default: {
          from = getFormattedDate(new Date(today.getFullYear(), today.getMonth(), 1))
          to = getFormattedDate(today)
        }
      }

      return { from, to }
    },

    updateActiveButton() {
      // Find which button matches current date range
      this.quickDateButtons.forEach(button => {
        const { from, to } = this.calculateDateRange(button.type)
        // Compare dates as strings in the same format
        const currentFrom = this.localRangePicker[0]
        const currentTo = this.localRangePicker[1]
        button.active = (from === currentFrom && to === currentTo)
      })
    },

    saveSettings() {
      try {
        const activeButton = this.quickDateButtons.find(btn => btn.active)
        // Format dates to YYYY-MM-DD before saving
        const formattedDates = Array.isArray(this.localRangePicker)
          ? this.localRangePicker.map(date => getFormattedDate(new Date(date)))
          : [getFormattedDate(new Date(this.localRangePicker)), getFormattedDate(new Date(this.localRangePicker))]

        const settings = {
          rangePicker: formattedDates,
          activeButton: activeButton ? activeButton.type : null,
        }
        localStorage.setItem('partnerStatsDateFilter', JSON.stringify(settings))
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: this.$t('errors.settings.title'),
            msg: this.$t('errors.settings.save_failed'),
            variant: 'danger',
          },
          { root: true },
        )
      }
    },

    loadSavedSettings() {
      try {
        const saved = localStorage.getItem('partnerStatsDateFilter')
        const settings = saved ? JSON.parse(saved) : null
        const selection = resolveStatsDateFilter(
          this.$route.query,
          settings,
          this.calculateDateRange,
          this.quickDateButtons.map(button => button.type),
        )
        if (selection) {
          this.localRangePicker = selection.range
          this.updateActiveButton()
          this.$emit('input', selection.range)
          if (selection.preset) {
            localStorage.setItem('partnerStatsDateFilter', JSON.stringify({
              rangePicker: selection.range,
              activeButton: selection.preset,
            }))
          }
          if (selection.preset && (
            this.$route.query.from !== selection.range[0]
            || this.$route.query.to !== selection.range[1]
            || this.$route.query.datePreset !== selection.preset
          )) {
            const query = {
              ...this.$route.query,
              from: selection.range[0],
              to: selection.range[1],
              datePreset: selection.preset,
            }
            window.history.replaceState(
              {},
              '',
              `${this.$route.path}?${new URLSearchParams(query).toString()}`,
            )
          }
        }
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: this.$t('errors.settings.title'),
            msg: this.$t('errors.settings.load_failed'),
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
  },
}
</script>

<style lang="scss">
.stats-date-range-filter {
  .col-picker {
    margin: 0 0.5rem;
    padding: 0 0.5rem;
    border: 1px solid #82868b;
    border-radius: 6px;
    .form-control {
      border: none;
      height: 27px;
    }
  }

  .quick-date-buttons {
    .btn{
      &:last-child {
        margin-left: -1px;
      }
      &.btn-outline-primary {
        background-color: rgba(115, 103, 240, 0.2);
      }
    }
  }
}
</style>
